import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { client } from 'graphql/client';
import { getActiveMissionV2 } from 'graphql/queries';
import { onUpdateActiveMissionByVin } from 'graphql/subscriptions';
import { SafeMissionV2Schema } from 'helper/v2Schemas';

import { AppError } from './errors';
import { fetchV2 } from './utils';

import type { GetActiveMissionV2Query, MissionV2 } from 'API';

const extractMission = (
    result: { __typename?: string; errorCode?: string; statusCode?: number } | null | undefined
): MissionV2 => {
    const isError = !result || result.__typename === 'ErrorResponse' || result.errorCode !== undefined;
    if (isError) {
        throw new AppError(result?.errorCode ?? 'Mission not found', {
            errorCode: result?.errorCode,
            statusCode: result?.statusCode,
        });
    }
    return result as unknown as MissionV2;
};

const parseMission = (data: MissionV2) => SafeMissionV2Schema().parse(data);

const getMissionV3QueryFn = (vin: string) =>
    fetchV2(
        () => client.graphql({ query: getActiveMissionV2, variables: { vin } }),
        (data: GetActiveMissionV2Query) =>
            data?.getActiveMissionV2 != null ? parseMission(extractMission(data.getActiveMissionV2)) : null,
        '[getMissionV3QueryFn]: Unexpected error occurred while fetching V3 mission'
    );

export const missionV3Api = createApi({
    reducerPath: 'missionV3',
    baseQuery: graphqlRequestBaseQuery({ url: '/graphql' }),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        getMissionV3: builder.query<MissionV2 | null, string>({
            queryFn: getMissionV3QueryFn,
        }),
        onMissionV3Subscription: builder.query<MissionV2 | null, string>({
            queryFn: getMissionV3QueryFn,
            onCacheEntryAdded: async (vin, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) => {
                // Waiting for a baseline before streaming updates is intentional and is RTK Query's
                // intended contract. Do not remove it without reading the rest of this comment.
                // `cacheDataLoaded` resolves only on a fulfilled query, so while the query keeps failing
                // no subscription is opened and this vehicle receives no live updates. That wait is not
                // abandoned though: it resolves on the FIRST successful fetch for this cache key, so a
                // later refetch (this api sets refetchOnMountOrArgChange, so a remount is enough) opens
                // the subscription at that point.
                //
                // TODO: REST-SUBSCRIPTIONS: two things are missing behind that wait.
                // 1. Nothing retries on its own. There is no pollingInterval, no refetchOnReconnect and
                //    no refetchOnFocus, so within a single mount a failed query never recovers and the
                //    user has to navigate away and back. Add a retry or a reconnect refetch.
                // 2. The wait is stricter than this endpoint needs. Every push carries the WHOLE mission
                //    rather than a delta, so there is no baseline to build on and a push could populate
                //    the cache by itself. If the replacement transport also sends full snapshots, it is
                //    safe to subscribe first and let the query fill in whatever arrives later — the
                //    newest message simply wins. Only keep the wait if the new transport sends deltas,
                //    because a delta with no baseline cannot be applied.
                // The user-visible half of this is already handled: useGetMissionData returns `currentData`
                // so a failed load no longer serves the previous vehicle's mission, and VehiclePage
                // forwards the error to an ErrorBoundary so it is reported instead of being dressed up as
                // "No mission assigned". What remains is purely the recovery behaviour above.
                // To verify: force the first mission request to fail and confirm the screen reports the
                // failure, then let a retry succeed and confirm live updates start flowing.
                await cacheDataLoaded;
                if (import.meta.env.DEV) {
                    await cacheEntryRemoved;
                } else {
                    const connection = client
                        .graphql({ query: onUpdateActiveMissionByVin, variables: { vin } })
                        .subscribe({
                            next: ({ data }) => {
                                if (!data.onUpdateActiveMissionByVin) {
                                    console.error(
                                        '[onMissionV3Subscription]: Please make sure all the attributes are returned in the mutation'
                                    );
                                    return;
                                }
                                const payload = data.onUpdateActiveMissionByVin.payload;
                                const hasMission = 'id' in payload && payload.id != null;
                                // TODO: REST-SUBSCRIPTIONS: parsing runs inside `updateCachedData`.
                                // Therefore a bad payload throws inside the immer producer. The cache is then left
                                // untouched and the update is dropped without anyone noticing: the screen silently
                                // keeps showing the previous mission and its old event log.
                                // How to fix when subscriptions move off GraphQL:
                                // 1. Parse the payload BEFORE calling `updateCachedData`, in its own step.
                                // 2. Use `safeParse` (or wrap `parseMission` in try/catch) so a bad payload
                                //    gives you a result object instead of an exception.
                                // 3. Only call `updateCachedData` when parsing worked. If it failed, log it and
                                //    leave the cache alone on purpose, so it is a decision and not an accident.
                                // 4. Also tell the UI the data is stale, otherwise the user keeps reading old
                                //    values as if they were live.
                                // 5. To verify: send one push with a broken field and check the app keeps
                                //    working and you can see in the console that the push was rejected.
                                updateCachedData(() =>
                                    hasMission
                                        ? parseMission(extractMission(data.onUpdateActiveMissionByVin.payload))
                                        : null
                                );
                            },
                            error: error => console.warn(error),
                        });
                    cacheEntryRemoved
                        .then(() => {
                            connection.unsubscribe();
                        })
                        .catch(error => console.error(error));
                }
            },
        }),
    }),
});

export const { useOnMissionV3SubscriptionQuery } = missionV3Api;
